import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { Command as CommandPrimitive } from 'cmdk';
import { useState } from 'react';

import {
    Command,
    CommandGroup,
    CommandItem,
    CommandList
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';

interface AppGroupFieldProps {
    value: string;
    groups: string[];
    onChange: (group: string) => void;
    disabled?: boolean;
}

export function AppGroupField({
    value,
    groups,
    onChange,
    disabled
}: AppGroupFieldProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const typed = search.trim();
    const canCreate =
        typed !== '' &&
        !groups.some((group) => group.toLowerCase() === typed.toLowerCase());

    const choose = (group: string) => {
        onChange(group);
        setSearch('');
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    aria-label="Group name"
                    disabled={disabled}
                    className="flex justify-between items-center gap-2 min-w-0 bg-transparent dark:bg-input/30 disabled:opacity-50 px-3 border border-input focus-visible:border-ring/30 rounded-lg outline-hidden focus-visible:ring-1 focus-visible:ring-ring/30 w-full h-[42px] text-[length:var(--fs-subtitle)] leading-[var(--lh-subtitle)] transition-colors disabled:cursor-not-allowed"
                >
                    <span
                        className={cn(
                            'flex-1 min-w-0 text-left truncate',
                            !value && 'text-muted-foreground'
                        )}
                    >
                        {value || 'No group'}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="bg-background! p-0 w-(--radix-popover-trigger-width)"
                align="start"
            >
                <Command className="bg-background!">
                    <div
                        className="flex items-center px-3 border-b"
                        cmdk-input-wrapper=""
                    >
                        <Plus className="mr-2 w-4 h-4 text-muted-foreground shrink-0" />
                        <CommandPrimitive.Input
                            value={search}
                            onValueChange={setSearch}
                            placeholder="Create…"
                            maxLength={200}
                            className="flex bg-transparent py-3 rounded-md outline-hidden w-full h-11 placeholder:text-muted-foreground text-sm"
                        />
                    </div>
                    <CommandList className="max-h-64">
                        {canCreate && (
                            <CommandGroup>
                                <CommandItem
                                    value={`create ${typed}`}
                                    onSelect={() => choose(typed)}
                                >
                                    <Plus className="mr-2 w-4 h-4 shrink-0" />
                                    <span className="min-w-0 truncate">
                                        Create “{typed}”
                                    </span>
                                </CommandItem>
                            </CommandGroup>
                        )}
                        <CommandGroup>
                            {groups.map((group) => (
                                <CommandItem
                                    key={group}
                                    value={group}
                                    onSelect={() => choose(group)}
                                >
                                    <span className="flex-1 min-w-0 truncate">
                                        {group}
                                    </span>
                                    {group === value && (
                                        <Check className="w-4 h-4 shrink-0" />
                                    )}
                                </CommandItem>
                            ))}
                            {value && !groups.includes(value) && (
                                <CommandItem
                                    value={value}
                                    onSelect={() => choose(value)}
                                >
                                    <span className="flex-1 min-w-0 truncate">
                                        {value}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label="Remove new group"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            choose('');
                                        }}
                                    >
                                        <X className="w-4 h-4 shrink-0" />
                                    </button>
                                </CommandItem>
                            )}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
